"use client";

import { ComponentLoggingEventParams } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { getColor } from "@faire/sdui/lib/getColor";
import { IBannerComponent } from "@faire/web-api--source/indigofair/data/IBannerComponent";
import { IResponsiveImages } from "@faire/web-api--source/indigofair/data/IResponsiveImages";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { Column } from "@faire/web/slate/Layout";
import { cn } from "@faire/web/tools/cn";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";

import { SDUIBannerTextContent } from "../BannerTextContent/SDUIBannerTextContent";

import { DecorativeImage } from "./DecorativeImage";

type DecorativeTextBannerProps = {
  bannerComponent: IBannerComponent;
  loggingData: ComponentLoggingEventParams;
};

const useGetContentWidthByResponsiveImages = (
  responsiveImages: IResponsiveImages | undefined
) => {
  const leftTabletImageWidth = Math.max(
    responsiveImages?.tablet_and_above?.[0]?.width ?? 0,
    responsiveImages?.tablet_and_above?.[2]?.width ?? 0
  );

  const rightTabletImageWidth = Math.max(
    responsiveImages?.tablet_and_above?.[3]?.width ?? 0,
    responsiveImages?.tablet_and_above?.[1]?.width ?? 0
  );

  const leftImageWidth = useResponsiveValue({
    mobileAndAbove: 0,
    tabletAndAbove: leftTabletImageWidth,
    desktopAndAbove: Math.max(
      leftTabletImageWidth,
      responsiveImages?.desktop_and_above?.[0]?.width ?? 0,
      responsiveImages?.desktop_and_above?.[2]?.width ?? 0
    ),
  });

  const rightImageWidth = useResponsiveValue({
    mobileAndAbove: 0,
    tabletAndAbove: rightTabletImageWidth,
    desktopAndAbove: Math.max(
      rightTabletImageWidth,
      responsiveImages?.desktop_and_above?.[3]?.width ?? 0,
      responsiveImages?.desktop_and_above?.[1]?.width ?? 0
    ),
  });

  return {
    leftImageWidth,
    rightImageWidth,
  };
};

export const SDUIDecorativeTextBannerRenderer: React.FC<
  DecorativeTextBannerProps
> = ({ bannerComponent, loggingData }) => {
  const { isMobile } = useViewport();
  const { style, responsive_images } = bannerComponent;

  const { background_color } = style ?? {};

  const { leftImageWidth, rightImageWidth } =
    useGetContentWidthByResponsiveImages(responsive_images);

  return (
    <Column
      className={cn(
        "min-h-[233px] w-full md:h-[423px]",
        isMobile ? "p-4" : "p-8"
      )}
      justify="center"
      align={isMobile ? "start" : "center"}
      style={{
        backgroundColor: getColor(background_color) ?? "transparent",
      }}
    >
      <DecorativeImage
        index={0}
        responsiveImage={responsive_images}
        position="top-left"
      />
      <DecorativeImage
        index={1}
        responsiveImage={responsive_images}
        position="top-right"
      />
      <DecorativeImage
        index={2}
        responsiveImage={responsive_images}
        position="bottom-left"
      />
      <DecorativeImage
        index={3}
        responsiveImage={responsive_images}
        position="bottom-right"
      />
      <div
        className="relative z-[1]"
        style={{
          maxWidth: `calc(100% - (${leftImageWidth + rightImageWidth}px))`,
        }}
      >
        <SDUIBannerTextContent
          className="items-start md:items-center"
          bannerComponent={bannerComponent}
          loggingData={loggingData}
          typgraphyHyphens="none"
        />
      </div>
    </Column>
  );
};
